import { commits } from '@/modules/core/app/helpers/store';
import { shallowCopyKeyedObject } from '@/modules/core/helper';
import { WidgetType } from '@/modules/ta/widget/widget.constants';
import { WidgetDataCache } from '@/modules/ta/widget/caches/WidgetDataCache';
import { User } from '@/modules/ta/user/models/User';

export const mutations = {
  /**
   * @param state
   * @param {boolean} value
   */
  setNeedsReload(state, value) {
    state.needsReload = value;
  },
  /**
   * @param state
   * @param {boolean} value
   */
  setFiltersNeedsReload(state, value) {
    state.filtersNeedsReload = value;
  },
  /**
   * @param state
   * @param {boolean} value
   */
  setIsInitializingDashboard(state, value) {
    state.isInitializingDashboard = value;
  },
  /**
   * @param state
   * @param {boolean} value
   */
  setFiltersInitialized(state, value) {
    state.filtersInitialized = value;
  },
  /**
   * @param state
   * @param {array} options
   */
  setFilterOptions(state, options) {
    state.filterOptions = options;
  },
  /**
   * @param state
   * @param {array} options
   */
  setActiveDashboardFilterOptions(state, options) {
    state.activeDashboardFilterOptions = options;
  },
  /**
   * @param state
   */
  resetActiveDashboardFilterOptions(state) {
    state.activeDashboardFilterOptions = null;
  },
  /**
   * @param state
   * @param {object} filters
   */
  setFilters(state, filters) {
    state.filters = filters;
  },
  /**
   * @param {DashboardState} state
   * @param {Dashboard} dashboard
   */
  setDashboard(state, { dashboard }) {
    // Preserve hydrated widgets before replacing dashboard
    const hydratedLayoutWidgets = {};
    if (state.dashboard && state.hydratedLayouts && state.hydratedLayouts.length) {
      state.hydratedLayouts.forEach((layoutId) => {
        const existingLayout = state.dashboard.layouts?.[layoutId];
        if (existingLayout && existingLayout.widgets) {
          const widgetCount = Object.keys(existingLayout.widgets).length;
          if (widgetCount > 0) {
            hydratedLayoutWidgets[layoutId] = existingLayout.widgets;
          }
        }
      });
    }

    Object.values(dashboard.layouts).forEach((layout) => {
      Object.values(layout.widgets).forEach((widget) => {
        // Clear any widget data cache on layout reset...
        WidgetDataCache.delete(widget.id);

        if (widget.metadata?.dynamic?.predefined_data?.data) {
          if (Object.hasOwn(widget.metadata.dynamic.predefined_data, 'total_data')) {
            widget.metadata.dynamic.predefined_data.data.total_data = Array.isArray(
              widget.metadata.dynamic.predefined_data.total_data
            )
              ? widget.metadata.dynamic.predefined_data.total_data[0]
              : widget.metadata.dynamic.predefined_data.total_data;
          }
          if (Object.hasOwn(widget.metadata.dynamic.predefined_data, 'sparkline_data')) {
            widget.appendSparkLineData(
              widget.metadata.dynamic.predefined_data.data,
              widget.metadata.dynamic.predefined_data.sparkline_data,
              true
            );
          }

          let { data } = widget.metadata.dynamic.predefined_data;
          if (widget.type === WidgetType.TYPE_ACCOUNTMANAGER) {
            data = new User(data);
          }

          WidgetDataCache.set(widget.id, data);
        }

        // handle ES acting as media widget in case of PPT export
        if (
          widget.type === WidgetType.TYPE_MEDIA &&
          !WidgetDataCache.has(widget.id) &&
          widget.metadata?.content
        ) {
          WidgetDataCache.set(widget.id, widget.metadata.content);
        }
      });
    });

    state.dashboard = dashboard;
    commits.dashboardLayout.setLayoutsFromDashboard({ dashboard });
    state.dashboard.layouts = Object.values(dashboard.layouts).reduce((accum, layout) => {
      accum[layout.id] = layout;
      return accum;
    }, {});

    // Restore hydrated widgets after setting the new dashboard
    if (Object.keys(hydratedLayoutWidgets).length > 0) {
      Object.entries(hydratedLayoutWidgets).forEach(([layoutId, widgets]) => {
        if (state.dashboard.layouts[layoutId]) {
          state.dashboard.layouts[layoutId].widgets = widgets;
          // Update the widget store with the restored widgets
          commits.widget.setWidgetsFromLayout({ layout: state.dashboard.layouts[layoutId] });
        }
      });
    }
  },
  setHasNonChatGPTWidgets(state, value) {
    if (state.dashboard) {
      state.dashboard.has_non_chatgpt_widgets = !!value;
    }
  },
  addLayout(state, { layout }) {
    state.dashboard.layouts[layout.id] = null;
    state.dashboard.layouts = shallowCopyKeyedObject(state.dashboard.layouts);
    commits.dashboardLayout.setLayout(layout);
  },
  removeLayout(state, layoutId) {
    delete state.dashboard.layouts[layoutId];
    state.dashboard.layouts = shallowCopyKeyedObject(state.dashboard.layouts);
  },
  setResizingWidget(state, widgetId = null) {
    state.isResizingWidgetId = widgetId;
  },
  updateHydratingLayout(state, layoutId, isHydrating) {
    if (isHydrating) {
      state.hydratingLayouts.push(layoutId);
    } else {
      state.hydratingLayouts = state.hydratingLayouts.filter((l) => l !== layoutId);
    }
  },
  addToHydratedLayouts(state, layoutId) {
    if (!state.hydratedLayouts.includes(layoutId)) {
      state.hydratedLayouts.push(layoutId);
    }
  },
  removeFromHydratedLayouts(state, layoutId) {
    if (state.hydratedLayouts.includes(layoutId)) {
      state.hydratedLayouts = state.hydratedLayouts.filter((id) => id !== layoutId);
    }
  },
  resetHydratedLayouts(state) {
    state.hydratedLayouts = [];
  },
  hydrateWidgetsInLayout(state, { layout }) {
    if (layout && state.dashboard.layouts[layout.id]) {
      state.dashboard.layouts[layout.id].widgets = layout.widgets;
    }
  },
  setCurrentLayoutId(state, id) {
    state.currentLayoutId = id;
  },
  setLayoutLoadStart(state, { layoutId, epochMs, perfMs }) {
    state.layoutLoadStartLayoutId = layoutId ?? null;
    state.layoutLoadStartEpochMs = epochMs ?? null;
    state.layoutLoadStartPerfMs = perfMs ?? null;
  },
  setExecutiveSummaryEnabled(state, value) {
    state.dashboard.executive_summary_enabled = value;
    commits.executivesummaries.toggleEnabled(value);
  },
  setHighlightedWidgetsOptions(state, value) {
    state.highlightedWidgetsOptions = value;
  },
  setDownloadAllLayouts(state, value) {
    state.exportAllLayouts = value;
  },
  setMapboxAccessToken(state, token) {
    state.mapBoxAccessToken = token;
  },
  setShowBenchmarkMessage(state, value) {
    if (!state.showBenchmarkMessage) {
      state.showBenchmarkMessage = value;
    }
  },
  resetShowBenchmarkMessage(state) {
    state.showBenchmarkMessage = false;
  },
  cleanup(state) {
    state.dashboard = null;
    // Do NOT null filterOptions here. Vue 2 destroys the old component (calling
    // cleanup) BEFORE mounting the new one, so nulling filterOptions creates a
    // race: the new page's mounted() → setFilterButton() runs after cleanup and
    // reads null, causing the filter icon to disappear on Data Source Overview
    // and similar non-dashboard pages. filterOptions are global (not per-dashboard)
    // and filtersInitialized is reset below, so beforeRouteEnter on the next page
    // will always re-fetch fresh options before calling next().
    state.activeDashboardFilterOptions = null;
    state.hydratedLayouts = [];
    state.hydratingLayouts = [];
    state.showBenchmarkMessage = false;
    // Reset so the next page's beforeRouteEnter re-fetches filter options.
    // Without resetting this flag the mixin skips the reload (it guards with
    // areFiltersInitialized()), leaving the filter panel showing only
    // "Current Dashboard".
    state.filtersInitialized = false;
  },
};
